import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ShoppingCart,
  Plus,
  Minus
} from 'lucide-react';
import { formatCurrency, getWholesaleUnit, getRetailUnit } from './posUtils';

const ProductSelectDialog = ({
  open,
  onOpenChange,
  selectedProduct,
  productSelectForm,
  setProductSelectForm,
  handleProductSelectAdd,
}) => {
  if (!selectedProduct) return null;

  const isFeeds = selectedProduct.category?.toLowerCase().includes('feed');
  const isWeightBased = productSelectForm.saleType === 'retail_weight' || productSelectForm.saleType === 'wholesale_weight';

  const calculateTotal = () => {
    if (isFeeds && productSelectForm.weightKg && parseFloat(productSelectForm.weightKg) > 0) {
      const weight = parseFloat(productSelectForm.weightKg);
      const conversionRate = selectedProduct.conversionRate || 50;
      const halfSackKg = conversionRate / 2;
      const isRetail = weight < halfSackKg;
      
      if (isRetail) {
        return weight * (selectedProduct.retailPrice || selectedProduct.sellingPricePerKg || 0);
      } else {
        const wholesalePrice = selectedProduct.wholesalePrice || selectedProduct.sellingPricePerSack || 0;
        return (weight / conversionRate) * wholesalePrice;
      }
    }
    
    const price = productSelectForm.saleType === 'wholesale' 
      ? (selectedProduct.wholesalePrice || selectedProduct.sellingPricePerSack) 
      : productSelectForm.saleType === 'half_sack'
      ? (selectedProduct.wholesalePrice || selectedProduct.sellingPricePerSack) / 2
      : (selectedProduct.retailPrice || selectedProduct.sellingPricePerKg);
    return price * (parseFloat(productSelectForm.quantity) || 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart size={20} className="text-emerald-600" />
            Add to Cart
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Product Info */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h3 className="font-semibold text-lg text-slate-900">{selectedProduct.name}</h3>
            <p className="text-sm text-slate-500">{selectedProduct.category}</p>
          </div>
          
          {/* Sale Type Selection */}
          <div className="space-y-2">
            <Label>Sale Type</Label>
            {isFeeds ? (
              <div className="space-y-4">
                {/* Weight Input for Feeds */}
                <div className="p-4 bg-slate-50 rounded-lg border-2 border-slate-200">
                  <Label className="text-sm font-semibold mb-2 block">Enter Weight (kg)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g., 12.5"
                      value={productSelectForm.weightKg}
                      onChange={(e) => {
                        const weight = e.target.value;
                        const conversionRate = selectedProduct.conversionRate || 50;
                        const halfSackKg = conversionRate / 2;
                        const isRetail = parseFloat(weight) < halfSackKg;
                        
                        setProductSelectForm({
                          ...productSelectForm, 
                          weightKg: weight,
                          saleType: isRetail ? 'retail_weight' : 'wholesale_weight',
                          quantity: parseFloat(weight) || 0
                        });
                      }}
                      className="text-xl font-bold text-center"
                      data-testid="weight-input"
                    />
                    <span className="text-lg font-medium text-slate-600">kg</span>
                  </div>
                  
                  {/* Price Preview */}
                  {productSelectForm.weightKg && parseFloat(productSelectForm.weightKg) > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg">
                      {(() => {
                        const weight = parseFloat(productSelectForm.weightKg) || 0;
                        const conversionRate = selectedProduct.conversionRate || 50;
                        const halfSackKg = conversionRate / 2;
                        const isRetailPrice = weight < halfSackKg;
                        
                        const wholesalePrice = selectedProduct.wholesalePrice || selectedProduct.sellingPricePerSack || 0;
                        const retailPrice = selectedProduct.retailPrice || selectedProduct.sellingPricePerKg || 0;
                        
                        let price, priceType, pricePerUnit;
                        if (isRetailPrice) {
                          price = weight * retailPrice;
                          priceType = 'Retail';
                          pricePerUnit = `${formatCurrency(retailPrice)}/kg`;
                        } else {
                          const sacks = weight / conversionRate;
                          price = sacks * wholesalePrice;
                          priceType = 'Wholesale';
                          pricePerUnit = `${formatCurrency(wholesalePrice)}/sack (${conversionRate}kg)`;
                        }
                        
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className={`text-sm font-semibold px-2 py-1 rounded ${isRetailPrice ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {priceType} Price
                              </span>
                              <span className="text-xs text-slate-500">{pricePerUnit}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-slate-600">{weight} kg</span>
                              <span className="text-xl font-bold text-green-600">{formatCurrency(price)}</span>
                            </div>
                            {!isRetailPrice && (
                              <p className="text-xs text-slate-500">
                                = {(weight / conversionRate).toFixed(3)} sacks x {formatCurrency(wholesalePrice)}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {isRetailPrice 
                                ? `Weight below ${halfSackKg}kg (half sack) = Retail pricing` 
                                : `Weight >= ${halfSackKg}kg (half sack) = Wholesale pricing`}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                
                {/* Quick buttons for common weights */}
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 25, 50].map(kg => {
                    const conversionRate = selectedProduct.conversionRate || 50;
                    const halfSackKg = conversionRate / 2;
                    const isRetailPrice = kg < halfSackKg;
                    return (
                      <button
                        key={kg}
                        type="button"
                        onClick={() => {
                          setProductSelectForm({
                            ...productSelectForm, 
                            weightKg: kg.toString(),
                            saleType: isRetailPrice ? 'retail_weight' : 'wholesale_weight',
                            quantity: kg
                          });
                        }}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all hover:shadow ${
                          isRetailPrice ? 'border-purple-200 hover:bg-purple-50 text-purple-600' : 'border-blue-200 hover:bg-blue-50 text-blue-600'
                        }`}
                      >
                        {kg} kg
                      </button>
                    );
                  })}
                </div>
                
                {/* Stock info */}
                <div className="flex justify-between text-xs text-slate-500 px-1">
                  <span>Sack stock: {selectedProduct.wholesaleStock || 0} ({(selectedProduct.wholesaleStock || 0) * (selectedProduct.conversionRate || 50)} kg)</span>
                  <span>Retail stock: {selectedProduct.retailStock || 0} kg</span>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setProductSelectForm({...productSelectForm, saleType: 'wholesale', quantity: 1})}
                  disabled={(selectedProduct.wholesaleStock || 0) <= 0}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    productSelectForm.saleType === 'wholesale' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${(selectedProduct.wholesaleStock || 0) <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-lg font-bold text-blue-600">
                    {formatCurrency(selectedProduct.wholesalePrice || selectedProduct.sellingPricePerSack)}
                  </div>
                  <div className="text-xs text-slate-500">per {getWholesaleUnit(selectedProduct).singular}</div>
                  <div className="text-sm mt-2 font-medium text-blue-600">
                    {selectedProduct.wholesaleStock || 0} {getWholesaleUnit(selectedProduct).plural} available
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setProductSelectForm({...productSelectForm, saleType: 'retail', quantity: 1})}
                  disabled={(selectedProduct.retailStock || 0) <= 0}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    productSelectForm.saleType === 'retail' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  } ${(selectedProduct.retailStock || 0) <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-lg font-bold text-purple-600">
                    {formatCurrency(selectedProduct.retailPrice || selectedProduct.sellingPricePerKg)}
                  </div>
                  <div className="text-xs text-slate-500">per {getRetailUnit(selectedProduct).singular}</div>
                  <div className="text-sm mt-2 font-medium text-purple-600">
                    {selectedProduct.retailStock || 0} {getRetailUnit(selectedProduct).plural} available
                  </div>
                </button>
              </div>
            )}
          </div>
          
          {/* Quantity Selection - Hide for Feeds weight-based input */}
          {!(isFeeds && (isWeightBased || productSelectForm.weightKg)) && (
          <div className="space-y-2">
            <Label>Quantity {productSelectForm.saleType === 'retail' && <span className="text-xs text-slate-500 ml-1">(type any amount: 0.30, 0.78, etc.)</span>}</Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  const step = productSelectForm.saleType === 'retail' ? 0.25 : 1;
                  const minVal = productSelectForm.saleType === 'retail' ? 0.01 : 1;
                  setProductSelectForm({
                    ...productSelectForm, 
                    quantity: Math.max(minVal, parseFloat((productSelectForm.quantity - step).toFixed(2)))
                  });
                }}
                disabled={productSelectForm.quantity <= (productSelectForm.saleType === 'retail' ? 0.01 : 1)}
              >
                <Minus size={18} />
              </Button>
              
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                value={productSelectForm.quantity}
                onChange={(e) => {
                  const inputVal = e.target.value;
                  if (inputVal === '' || inputVal === '.') {
                    setProductSelectForm({...productSelectForm, quantity: inputVal});
                    return;
                  }
                  const parsed = parseFloat(inputVal);
                  if (!isNaN(parsed)) {
                    let maxStock;
                    if (productSelectForm.saleType === 'wholesale') {
                      maxStock = selectedProduct.wholesaleStock || 0;
                    } else if (productSelectForm.saleType === 'half_sack') {
                      maxStock = (selectedProduct.wholesaleStock || 0) * 2;
                    } else {
                      maxStock = selectedProduct.retailStock || 0;
                    }
                    const minVal = productSelectForm.saleType === 'retail' ? 0.01 : 1;
                    const val = Math.min(Math.max(minVal, parsed), maxStock);
                    setProductSelectForm({...productSelectForm, quantity: val});
                  }
                }}
                onBlur={(e) => {
                  const parsed = parseFloat(e.target.value);
                  if (isNaN(parsed) || parsed <= 0) {
                    const minVal = productSelectForm.saleType === 'retail' ? 0.25 : 1;
                    setProductSelectForm({...productSelectForm, quantity: minVal});
                  } else {
                    setProductSelectForm({...productSelectForm, quantity: parseFloat(parsed.toFixed(2))});
                  }
                }}
                className="w-24 text-center text-lg font-semibold"
              />
              
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  let maxStock;
                  if (productSelectForm.saleType === 'wholesale') {
                    maxStock = selectedProduct.wholesaleStock || 0;
                  } else if (productSelectForm.saleType === 'half_sack') {
                    maxStock = (selectedProduct.wholesaleStock || 0) * 2;
                  } else {
                    maxStock = selectedProduct.retailStock || 0;
                  }
                  const step = productSelectForm.saleType === 'retail' ? 0.25 : 1;
                  const currentQty = parseFloat(productSelectForm.quantity) || 0;
                  setProductSelectForm({
                    ...productSelectForm, 
                    quantity: Math.min(maxStock, parseFloat((currentQty + step).toFixed(2)))
                  });
                }}
                disabled={(parseFloat(productSelectForm.quantity) || 0) >= (
                  productSelectForm.saleType === 'wholesale' 
                    ? (selectedProduct.wholesaleStock || 0) 
                    : productSelectForm.saleType === 'half_sack'
                    ? (selectedProduct.wholesaleStock || 0) * 2
                    : (selectedProduct.retailStock || 0)
                )}
              >
                <Plus size={18} />
              </Button>
              
              <span className="text-sm text-slate-500">
                {productSelectForm.saleType === 'wholesale' 
                  ? getWholesaleUnit(selectedProduct).plural 
                  : productSelectForm.saleType === 'half_sack'
                  ? '1/2 sacks'
                  : getRetailUnit(selectedProduct).plural}
              </span>
            </div>
            
            {/* Quick quantity buttons for retail */}
            {productSelectForm.saleType === 'retail' && (
              <div className="flex flex-wrap gap-2 mt-2">
                {[0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 5].map(qty => (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setProductSelectForm({...productSelectForm, quantity: qty})}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      productSelectForm.quantity === qty
                        ? 'bg-purple-100 border-purple-400 text-purple-700 font-semibold'
                        : 'bg-white border-slate-200 hover:border-purple-300 text-slate-600'
                    }`}
                  >
                    {qty} {getRetailUnit(selectedProduct).singular}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
          
          {/* Total */}
          <div className="p-4 bg-emerald-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Total</span>
              <span className="text-2xl font-bold text-emerald-600">
                {formatCurrency(calculateTotal())}
              </span>
            </div>
            {productSelectForm.saleType === 'half_sack' && (
              <p className="text-xs text-orange-600 mt-1">1/2 sack = half of full sack price</p>
            )}
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleProductSelectAdd}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus size={18} className="mr-2" />
            Add to Cart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSelectDialog;
